import { ErrorMessage } from "@hookform/error-message"
import {
  ApiError,
  Button,
  ButtonColor,
  Control,
  cn,
  Field,
  generateRuleList,
  InfoPanel,
  InfoPanelColor,
  Input,
  SelectControl,
  StaticValue,
  useCloseModal,
  useToastManager,
  uuidPattern
} from "@quortex/front-common"
import { useMutation } from "@tanstack/react-query"
import { type FC, useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import type { QoEProviderFormType } from "@/pages/ContentSteering/Settings/form"
import { useCreateQoEProviderConfig, useUpdateQoEProviderConfig } from "@/queries/qoe"

interface Props {
  qoeProvider?: api.switchns.QoeProvider
}

const QoEProviderForm: FC<Props> = ({ qoeProvider }) => {
  const closeModal = useCloseModal()
  const toastManager = useToastManager()
  const { mutateAsync: createQoEProviderConfig } = useMutation(useCreateQoEProviderConfig())
  const { mutateAsync: updateQoEProviderConfig } = useMutation(useUpdateQoEProviderConfig())

  const form = useForm<QoEProviderFormType>({
    defaultValues: qoeProvider
      ? qoeProvider
      : { mux: { accessToken: "", secretKey: "" }, type: "mux" }
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: -
  useEffect(() => {
    tryAuthent(form.getValues(), true)
  }, [])

  const tryAuthent = async (data: QoEProviderFormType, onLoad: boolean = false) => {
    try {
      if (qoeProvider) await updateQoEProviderConfig(data)
      else await createQoEProviderConfig(data)

      if (!onLoad) toastManager.success({ message: "QoE provider connected" })
      form.reset(data)
      closeModal()
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        const errorList: api.switchns.WorkspaceValidationError[] =
          e?.jsonData as unknown as api.switchns.WorkspaceValidationError[]

        const muxError = errorList.find(error => error.field === "$.mux")
        if (muxError?.errorCode === "mux_connection_unavailable") {
          form.setError("root", {
            message: "Try connect again or contact support.",
            type: "manual"
          })
          return
        }
        if (muxError?.errorCode === "mux_connection_unauthorized") {
          form.setError("root", {
            message: onLoad
              ? "We couldn't validate the information you entered. Verify the provider type, access token, and secret key and make sure they are still valid."
              : "We couldn't connect to MUX using the details you entered. Check the access token and secret key and try again.",
            type: "manual"
          })
          return
        }
      } else {
        toastManager.error({ apiError: e })
      }
    }
  }

  const onSubmit = form.handleSubmit(async data => {
    tryAuthent(data)
  })

  const options = [{ label: "MUX", value: "mux" }]

  return (
    <form className="flex w-full flex-col" onSubmit={onSubmit}>
      <FormProvider {...form}>
        <ErrorMessage
          name="root"
          render={error => (
            <InfoPanel
              className="mb-sm"
              color={InfoPanelColor.DANGER}
              data-testid="qoe-error-panel"
              title="Unable to connect"
            >
              {error.message}
            </InfoPanel>
          )}
        />
        <div className="flex flex-col gap-sm">
          <Field className="max-w-80" label="Provider type" name="type">
            {fieldName =>
              qoeProvider ? (
                <StaticValue
                  name={fieldName}
                  value={options.find(option => option.value === form.watch(fieldName))?.label}
                />
              ) : (
                <SelectControl
                  data-testid="provider-type-select"
                  defaultValue={form.watch(fieldName)}
                  name={fieldName}
                  options={options}
                />
              )
            }
          </Field>
          <Field className="w-80" label="Access token" name="mux.accessToken">
            {fieldName => (
              <Control
                as={Input}
                {...form.register(
                  fieldName,
                  generateRuleList({
                    pattern: uuidPattern,
                    required: true
                  })
                )}
                data-testid="access-token"
                placeholder="44c819de-4add-4c9f-b2e9-384a0a71bede"
                type="text"
                onChange={e => {
                  form.clearErrors()
                  form.setValue(fieldName, e.target.value, { shouldDirty: true })
                }}
              />
            )}
          </Field>

          <Field className="w-80" label="Secret key" name="mux.secretKey">
            {fieldName => (
              <Control
                as={Input}
                {...form.register(
                  fieldName,
                  generateRuleList({
                    pattern: /^[A-Za-z0-9+/]{40,}={0,2}$/,
                    required: true
                  })
                )}
                data-testid="secret-key"
                placeholder="INKxCoZ+cX6l1yrR6vqzYHVaeFEcqvZShznWM1U/No8KsV7h6Jxu1XXuTUQ91sdiGONK3H7NE7H"
                type="text"
                onChange={e => {
                  form.clearErrors()
                  form.setValue(fieldName, e.target.value, { shouldDirty: true })
                }}
              />
            )}
          </Field>
        </div>

        <div
          className={cn(
            "flex gap-sm",
            qoeProvider && (form.formState.isDirty ? "block" : "hidden"),
            qoeProvider ? "mt-md" : "mt-lg"
          )}
        >
          <Button
            color={ButtonColor.SECONDARY}
            data-testid="cancel-btn"
            type="button"
            onClick={qoeProvider ? () => form.reset() : closeModal}
          >
            Cancel
          </Button>
          <Button
            color={ButtonColor.PRIMARY}
            data-testid="connect-btn"
            isLoading={form.formState.isSubmitting}
            type="submit"
          >
            Connect
          </Button>
        </div>
      </FormProvider>
    </form>
  )
}

export default QoEProviderForm
